import { reactive, computed, ref, watch, type Ref } from 'vue'
import type {
  ProfileState, PushUpsState, CoreState, CardioState, WhtrState,
  ScoreResult,
} from '../lib/types'
import {
  PU_STD_M, PU_STD_F, PU_HR_M, PU_HR_F,
  SITUP_M, SITUP_F, CLRC_M, CLRC_F, PLANK_M, PLANK_F,
  RUN_M, RUN_F, HAMR_M, HAMR_F,
} from '../lib/scoringTables'
import {
  getAG, lookupHigh, lookupLow, lookupWHtR, parseRunTime, parsePlankTime,
} from '../lib/scoringHelpers'
import { buildScoreResult } from '../lib/buildScoreResult'
import { parseAgeField, parseIntField } from '../lib/scoringInput'

interface UseScoringOptions {
  autoSave?: boolean
  setGlobalScore?: boolean
  persistInputsKey?: string
  persistOnChange?: boolean
  /** When false, skip loading last inputs from localStorage (UFPM per-member session drafts). */
  restorePersistedInputs?: boolean
  onCalculated?: (score: ScoreResult) => void
}

export function useScoring(options: UseScoringOptions = {}) {
  const autoSave = options.autoSave ?? true
  const setGlobalScore = options.setGlobalScore ?? true
  const profile = reactive<ProfileState>({ sex: 'male', age: '' })
  const pushUps = reactive<PushUpsState>({ type: 'standard', reps: '', exempt: false })
  const core    = reactive<CoreState>({ type: 'situp', reps: '', plankMin: '', plankSec: '', exempt: false })
  const cardio  = reactive<CardioState>({ type: 'run', runMin: '', runSec: '', shuttles: '', exempt: false })
  const whtr    = reactive<WhtrState>({ waist: '', height: '', exempt: false })

  const result: Ref<ScoreResult | null> = ref(null)

  // Persist last test inputs (profile + component values) so the dashboard / record test remembers the last entered score values across reloads/app restarts.
  const LAST_INPUTS_KEY = options.persistInputsKey ?? 'af_pt_last_test_inputs'
  const restorePersistedInputs = options.restorePersistedInputs ?? true
  if (restorePersistedInputs) {
    try {
      const raw = localStorage.getItem(LAST_INPUTS_KEY)
      if (raw) {
        const last = JSON.parse(raw)
        if (last.profile) {
          if (last.profile.sex != null) profile.sex = last.profile.sex
          if (last.profile.age != null && String(last.profile.age).trim() !== '') {
          profile.age = String(last.profile.age)
        }
        }
        if (last.pushUps) Object.assign(pushUps, last.pushUps)
        if (last.core) Object.assign(core, last.core)
        if (last.cardio) Object.assign(cardio, last.cardio)
        if (last.whtr) Object.assign(whtr, last.whtr)
      }
    } catch { /* ignore bad storage */ }
  }

  function saveLastInputs() {
    try {
      const data = {
        profile: { sex: profile.sex, age: profile.age },
        pushUps: { ...pushUps },
        core: { ...core },
        cardio: { ...cardio },
        whtr: { ...whtr },
      }
      localStorage.setItem(LAST_INPUTS_KEY, JSON.stringify(data))
    } catch { /* quota or unavailable */ }
  }

  const componentScores = computed(() => {
    const ageNum = parseAgeField(profile.age)
    if (ageNum === null) return { pu: null, core: null, cardio: null, whtr: null }
    const ag = getAG(ageNum)

    let pu: number | null = null
    if (!pushUps.exempt) {
      const reps = parseIntField(pushUps.reps)
      const table = pushUps.type === 'standard'
        ? (profile.sex === 'male' ? PU_STD_M : PU_STD_F)
        : (profile.sex === 'male' ? PU_HR_M : PU_HR_F)
      pu = lookupHigh(table, ag, reps)
    }

    let core_: number | null = null
    if (!core.exempt) {
      if (core.type === 'plank') {
        const secs = parsePlankTime(core.plankMin, core.plankSec)
        core_ = lookupHigh(profile.sex === 'male' ? PLANK_M : PLANK_F, ag, secs)
      } else {
        const reps = parseIntField(core.reps)
        const table = core.type === 'situp'
          ? (profile.sex === 'male' ? SITUP_M : SITUP_F)
          : (profile.sex === 'male' ? CLRC_M : CLRC_F)
        core_ = lookupHigh(table, ag, reps)
      }
    }

    let cardio_: number | null = null
    if (!cardio.exempt) {
      if (cardio.type === 'run') {
        const secs = parseRunTime(cardio.runMin, cardio.runSec)
        cardio_ = secs > 0 ? lookupLow(profile.sex === 'male' ? RUN_M : RUN_F, ag, secs) : 0
      } else {
        const shuttles = parseIntField(cardio.shuttles)
        cardio_ = shuttles > 0 ? lookupHigh(profile.sex === 'male' ? HAMR_M : HAMR_F, ag, shuttles) : 0
      }
    }

    let whtr_: number | null = null
    if (!whtr.exempt) {
      const w = parseFloat(whtr.waist)
      const h = parseFloat(whtr.height)
      whtr_ = (w > 0 && h > 0) ? lookupWHtR(Math.floor((w / h) * 100) / 100) : 0
    }

    return { pu, core: core_, cardio: cardio_, whtr: whtr_ }
  })

  if (options.persistOnChange) {
    watch([profile, pushUps, core, cardio, whtr], () => saveLastInputs(), { deep: true })
  }

  function calculate(): void {
    const scored = buildScoreResult({ profile, pushUps, core, cardio, whtr })
    if (!scored) {
      window.showToast?.('Enter a valid age from 17 to 70.')
      return
    }

    result.value = scored
    if (setGlobalScore) {
      window.lastScore = scored
      window.updateAISummary?.()
    }
    options.onCalculated?.(scored)
    if (autoSave) window.showScoreSavePrompt?.()
    saveLastInputs()
  }

  function clear(): void {
    profile.age     = ''
    pushUps.reps    = ''
    core.reps       = ''
    core.plankMin   = ''
    core.plankSec   = ''
    cardio.runMin   = ''
    cardio.runSec   = ''
    cardio.shuttles = ''
    whtr.waist      = ''
    whtr.height     = ''
    pushUps.exempt  = false
    core.exempt     = false
    cardio.exempt   = false
    whtr.exempt     = false
    result.value    = null
  }

  return { profile, pushUps, core, cardio, whtr, result, componentScores, calculate, clear }
}
